import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from 'react-router-dom'
import { ErrorPage } from '@/pages/errors/ErrorPage'
import { NotFoundPage } from '@/pages/errors/NotFoundPage'
import DashboardLayout from '@/pages/Layout'
import TopicListPage from '@/pages/topic'
import ModernDiscoverPage from '@/pages/topic/ModernDiscoverPage'
import DiscoverHome from '@/pages/topic/DiscoverHome'
import ModernWorkspace from '@/pages/home/ModernWorkspace'
import CleanWorkspace from '@/pages/home/CleanWorkspace'
import CirclesPage from '@/pages/circles/Circles'
import UserProfile from '@/pages/user-profile'
import EditTopic from '@/pages/topic-management/Edit'
import CreateTopic from '@/pages/topic-management/Create'
import ShareInteractionsPage from '@/pages/share'
import UploadsPage from '@/pages/uploads'
import PatternLayout from '@/pages/pattern/Layout'
import PatternPage from '@/pages/pattern'
import TopicDetailsLayout from '@/pages/topic-details/Layout'
import { TopicPreviewBase } from '@/pages/topic-details/TopicPreviewBase'
import Home from '@/pages/home'
import { PrivacyType, TopicType } from '@/utils/constants'
import { albumRouteElements } from '@/features/album'
import LandingV2Layout from '@/pages/marketing/landing-v2/LandingV2Layout'
import { PricingPage } from '@/pages/marketing/PricingPage'
import AuthPreviewPage from '@/pages/auth/AuthPreviewPage'
import { TermsPage } from '@/pages/marketing/legal/TermsPage'
import { PrivacyPage } from '@/pages/marketing/legal/PrivacyPage'

export const RootRouter = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* 2026 album product layer — parallel to legacy workspace */}
      {albumRouteElements}

      {/* Landing v2 (Spectrum design system) — the live marketing home page. */}
      <Route path='/' element={<LandingV2Layout />}>
        <Route path='auth' />
      </Route>
      <Route path='/landing-v2' element={<Navigate to='/' replace />} />

      {/* Marketing — public pages, no auth required */}
      <Route path='/pricing' element={<PricingPage />} />
      <Route path='/terms' element={<TermsPage />} />
      <Route path='/privacy' element={<PrivacyPage />} />

      {/* Guest Pattern Editor - No auth required */}
      <Route path='/pattern' element={<PatternLayout />}>
        <Route path='' element={<PatternPage />} />
      </Route>

      <Route path='/login' element={<Navigate to='/auth?step=login' replace />} />
      <Route path='/signup' element={<Navigate to='/auth?step=signup' replace />} />
      <Route
        path='/forgot-password'
        element={<Navigate to='/auth?step=forgot-password' replace />}
      />

      {/* Preview-only: full-page rebuild of the auth screens (no logic
          change vs. the live AuthDialog flows). Not linked anywhere yet. */}
      <Route path='/auth-preview' element={<Navigate to='/auth-preview/login' replace />} />
      <Route path='/auth-preview/:step' element={<AuthPreviewPage />} />

      <Route path='/' element={<DashboardLayout />} errorElement={<ErrorPage />}>
        <Route element={<TopicListPage />} />
        <Route path='home' element={<TopicListPage />} />
        <Route path='topic' element={<TopicListPage />} />
        <Route path='topic/edit/:id' element={<EditTopic />} />
        <Route path='share' element={<ShareInteractionsPage />} />
        <Route path='topic/create' element={<CreateTopic />} />
        <Route path='circles' element={<CirclesPage />} />
        <Route path='uploads' element={<UploadsPage />} />

        <Route path='profile'>
          {/* /profile/me and /profile/v2 were superseded by /settings and
              /profile/:userId (new design). Redirect so bookmarks still work. */}
          <Route path='me' element={<Navigate to='/settings' replace />} />
          <Route path='v2' element={<Navigate to='/settings' replace />} />
          <Route path='user' element={<UserProfile />} />
        </Route>

        {/* /search moved to the album shell (features/search) — see albumRouteElements above */}

        {/* `pattern` used to be nested here as a child route, but
            TopicPreviewBase never renders an <Outlet/> — it picks
            PatternTopicPreview vs TopicDetailsView itself from the
            `topicType` query param — and nothing navigates to the literal
            `/preview/pattern` URL, so that route was dead. Removed per the
            guest-surface audit rather than building the guest-safe version
            of /preview for now. */}
        <Route path='preview' element={<TopicDetailsLayout />}>
          <Route path='' element={<TopicPreviewBase />} />
        </Route>

        {/* Authenticated Pattern Editor - inside DashboardLayout */}
        <Route path='editor/pattern' element={<PatternLayout />}>
          <Route path='' element={<PatternPage />} />
        </Route>
      </Route>

      <Route path='/discover' element={<DashboardLayout />} errorElement={<ErrorPage />}>
        <Route
          path='home'
          element={<DiscoverHome topicType={TopicType.ALL} privacyType={PrivacyType.PUBLIC} />}
        />

        <Route
          path='pattern'
          element={<DiscoverHome topicType={TopicType.PATTERN} privacyType={PrivacyType.PUBLIC} />}
        />
        <Route
          path='custom_print'
          element={
            <TopicListPage topicType={TopicType.CUSTOM_PRINTS} privacyType={PrivacyType.PUBLIC} />
          }
        />

        {/* <Route
          path='quilt'
          element={<TopicListPage topicType={TopicType.QUILT} privacyType={PrivacyType.PUBLIC} />}
        /> */}
        <Route
          path='album'
          element={<DiscoverHome topicType={TopicType.ALBUM} privacyType={PrivacyType.PUBLIC} />}
        />

        <Route
          path='gallery'
          element={<DiscoverHome topicType={TopicType.GALLERY} privacyType={PrivacyType.PUBLIC} />}
        />
      </Route>

      <Route path='/workspace' element={<DashboardLayout />} errorElement={<ErrorPage />}>
        <Route path='all' element={<CleanWorkspace />} />
        {/* Alternative versions: <Route path='all' element={<ModernWorkspace />} /> or <Route path='all' element={<Home />} /> */}
        <Route
          path='album'
          element={<TopicListPage topicType={TopicType.ALBUM} privacyType={PrivacyType.PRIVATE} />}
        />
        <Route path='upload' element={<UploadsPage />} />
        <Route
          path='pattern'
          element={
            <TopicListPage topicType={TopicType.PATTERN} privacyType={PrivacyType.PRIVATE} />
          }
        />
        <Route
          path='custom_print'
          element={
            <TopicListPage topicType={TopicType.CUSTOM_PRINTS} privacyType={PrivacyType.PRIVATE} />
          }
        />
        <Route
          path='gallery'
          element={
            <TopicListPage topicType={TopicType.GALLERY} privacyType={PrivacyType.PRIVATE} />
          }
        />
      </Route>

      <Route path='/projects' element={<DashboardLayout />}>
        <Route
          path='quilt'
          element={<TopicListPage topicType={TopicType.QUILT} privacyType={PrivacyType.PRIVATE} />}
        />
        <Route
          path='gallery'
          element={
            <TopicListPage topicType={TopicType.GALLERY} privacyType={PrivacyType.PRIVATE} />
          }
        />
      </Route>

      {/* <Route path='/' element={<TopicDetailsLayout />}>
        <Route path='preview' element={<TopicPreviewBase />} />
      </Route>

      <Route path='/' element={<PatternLayout />}>
        <Route path='pattern' element={<PatternPage />} />
      </Route> */}

      {/* Catch-all — any unmatched path renders the 404 page */}
      <Route path='*' element={<NotFoundPage />} />
    </>,
  ),
)
